import {
  NexusPricingEntry,
  NexusProduct,
  NexusPromotion,
} from "@/types/nexus-product";
import { formatPrice } from "@/utils/formatePrice";

export function findApplicablePromotions(
  promotions: NexusPromotion[],
  productId: string,
  variantId: string | null,
  categories: string[],
): NexusPromotion[] {
  return promotions.filter((promotion) => {
    if (promotion.targetType === "PRODUCT") {
      if (!variantId) return false;
      return promotion.productTargets.some(
        (target) =>
          target.productId === productId && target.variantId === variantId,
      );
    }
    if (promotion.targetType !== "CATEGORY") return false;
    return promotion.categoryIds.some((categoryId) =>
      categories.includes(categoryId),
    );
  });
}

export function resolveCartPromotion(
  promotions: NexusPromotion[],
  subtotalGrosze: number,
): NexusPromotion | null {
  return pickBestPromotion(
    promotions.filter(
      (promotion) =>
        promotion.targetType === "CART" &&
        subtotalGrosze >= (promotion.minCartValueGross ?? 0),
    ),
  );
}

export function computeCartDiscountGrosze(
  subtotalGrosze: number,
  promotion: NexusPromotion,
): number {
  const discount =
    promotion.discountType === "PERCENT"
      ? Math.round(subtotalGrosze * ((promotion.discountPercent ?? 0) / 100))
      : (promotion.discountAmountGross ?? 0);
  const cappedDiscount = Math.min(
    discount,
    promotion.maxDiscountAmountGross ?? discount,
  );

  return Math.max(0, Math.min(cappedDiscount, subtotalGrosze));
}

export function pickBestPromotion(
  candidates: NexusPromotion[],
): NexusPromotion | null {
  if (!candidates.length) return null;
  return [...candidates].sort((a, b) => {
    if (a.priority !== b.priority) return a.priority - b.priority;
    if (a.targetType !== b.targetType) {
      return a.targetType === "PRODUCT" ? -1 : 1;
    }
    return 0;
  })[0];
}

export function resolvePromotion(
  promotions: NexusPromotion[],
  productId: string,
  variantId: string | null,
  categories: string[],
): NexusPromotion | null {
  return pickBestPromotion(
    findApplicablePromotions(promotions, productId, variantId, categories),
  );
}

export function computeDiscountedPriceGrosze(
  priceGrosze: number,
  promotion: NexusPromotion,
): number {
  if (promotion.discountType === "PERCENT") {
    const discount = Math.round(
      priceGrosze * ((promotion.discountPercent ?? 0) / 100),
    );
    return Math.max(0, priceGrosze - discount);
  }
  const discount = Math.min(promotion.discountAmountGross ?? 0, priceGrosze);
  return Math.max(0, priceGrosze - discount);
}

export function formatPromotionBadgeLabel(promotion: NexusPromotion): string {
  if (promotion.discountType === "PERCENT") {
    return `-${promotion.discountPercent}%`;
  }
  return `-${formatPrice((promotion.discountAmountGross ?? 0) / 100)}`;
}

export function getBasePricingEntry(
  nexusProduct: NexusProduct,
): NexusPricingEntry | null {
  const active = Object.values(nexusProduct.pricing).filter(
    (entry) => !entry.disabled,
  );
  if (!active.length) return null;
  return active.reduce((cheapest, entry) =>
    entry.price < cheapest.price ? entry : cheapest,
  );
}

export type ResolvedCardPromotion = {
  promotion: NexusPromotion;
  originalPriceGrosze: number;
  discountedPriceGrosze: number;
};

export function resolveProductCardPromotion(
  nexusProduct: NexusProduct,
  promotions: NexusPromotion[],
): ResolvedCardPromotion | null {
  if (!promotions.length) return null;
  const entry = getBasePricingEntry(nexusProduct);
  if (!entry) return null;
  const variantId = entry.variantIds[0] ?? null;
  const promotion = resolvePromotion(
    promotions,
    nexusProduct.id,
    variantId,
    nexusProduct.categories,
  );
  if (!promotion) return null;
  return {
    promotion,
    originalPriceGrosze: entry.price,
    discountedPriceGrosze: computeDiscountedPriceGrosze(entry.price, promotion),
  };
}
